import React from 'react';
import { motion } from 'framer-motion';

interface Project {
  title: string;
  description: string;
  technologies: string[];
  image: string;
  github?: string;
  demo?: string;
  isGitHubCard?: boolean;
}

const projects: Project[] = [
  {
    title: "RL-based Multigent Code Optimizer",
    description: "Designed and trained a 7-action PPO agent for sequential decision optimization, achieving 21% runtime and 14% memory gains. Built structured 64-dim state encoder using AST metrics, embeddings, and execution baselines for multi-objective policy learning. Implemented multi-agent LLM refinement with entropy-regularized policy selection to stabilize exploration and prevent collapse. Deployed Dockerized sandbox (8s timeout, 512MB cap) with JWT-authenticated FastAPI backend and Supabase rate limiting.",
    technologies: ["PPO", "Reinforcement Learning", "FastAPI", "Docker", "Supabase", "AST", "LLM"],
    image: "images/rlcodeoptimizer.png",
    github: "https://github.com/Reemostat/Rl_Multi_Agent_Code_Optimizer"
  },
  {
    title: "AgenSummarizerAI",
    description: "Built an AI meeting summarizer using GPT and AssemblyAI, reducing manual note-taking time by 50%. Optimized RAG pipelines with Pinecone embeddings and prompt caching, achieving 0.85 cosine similarity. Designed an efficient backend system supporting 50+ meetings with summaries generated in under 5 seconds.",
    technologies: ["GPT", "AssemblyAI", "RAG", "Pinecone", "Python", "FastAPI"],
    image: "images/SummarAI.png",
    github: "https://github.com/Reemostat/AgenSummerizerAIv"
  },
  {
    title: "MedSeg-LLM",
    description: "Constructed Mask R-CNN pipeline on 500+ CT scans, generating pixel-level segmentation masks with 93% IoU accuracy. Fine-tuned LLaMA-2 on segmented clinical findings, generating automated diagnostic reports, reducing manual reporting workload by 70%. Accelerated inference pipeline using batch processing and caching, deployed via FastAPI, reducing end-to-end runtime by 40%.",
    technologies: ["Mask R-CNN", "LLaMA-2", "PyTorch", "FastAPI", "Computer Vision", "Medical Imaging"],
    image: "images/gemini.jpg",
    github: "https://github.com/Reemostat/medseg_llm_v2/tree/main"
  },
  {
    title: "Transfer of Emotion Contagion through Social Media",
    description: "Created a Dataset of 17000+ tweets with images and text via web scraping, cleaned using Regular Expressions. Implemented a pipeline to extract emotional data from facial expressions and text with DeepFace and Pytesseract. Modified a BERTweet model to predict ten emotions using metadata and NLTK, achieving 85% accuracy.",
    technologies: ["BERTweet", "DeepFace", "Pytesseract", "NLTK", "Web Scraping", "Multimodal AI"],
    image: "images/Transfer of emoticon.png",
    github: "https://github.com/Reemostat/EmotionalContagion-master"
  },
  {
    title: "GitHub",
    description: "Visit My GitHub",
    technologies: [],
    image: "",
    github: "https://github.com/Reemostat",
    isGitHubCard: true
  }
];

const Projects: React.FC = () => {
  return (
    <section id="projects" className="py-20 relative overflow-hidden">
      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">My Projects</span>
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto rounded-full"></div>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={project.title}
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
              className={`bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-200 ${project.isGitHubCard ? 'cursor-pointer bg-black border-black' : ''}`}
              onClick={() => project.isGitHubCard && window.open(project.github, '_blank')}
            >
              {project.isGitHubCard ? (
                <div className="h-full min-h-[400px] flex items-center justify-center bg-black hover:bg-gray-900 transition-colors duration-300">
                  <motion.svg 
                    className="w-12 h-12 text-white" 
                    fill="currentColor" 
                    viewBox="0 0 24 24"
                    animate={{
                      rotate: [0, 10, -10, 10, -10, 0],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
                  </motion.svg>
                  <br />
                  <span className="text-white" style={{ fontSize: '1.25rem', fontWeight: 'medium', paddingLeft: '10px' }}>Visit My GitHub</span>
                </div>
              ) : (
                <>
                  <div className="relative h-48 overflow-hidden group">
                    <motion.img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover"
                      whileHover={{ 
                        scale: 1.15,
                        rotate: 2,
                      }}
                      transition={{ 
                        duration: 0.5,
                        ease: "easeOut"
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    {project.github && (
                      <motion.a
                        href={project.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.95 }}
                        className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors duration-200 z-10"
                        aria-label="View on GitHub"
                      >
                        <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
                        </svg>
                      </motion.a>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="text-xl font-semibold text-white mb-2">{project.title}</h3>
                      <div className="flex flex-wrap gap-2">
                        {project.technologies.map((tech) => (
                          <span
                            key={tech}
                            className="px-2 py-1 bg-white/20 text-white text-sm rounded-full backdrop-blur-sm"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-gray-600 mb-4">{project.description}</p>
                    {project.github && (
                      <motion.a
                        href={project.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="inline-flex items-center gap-2 text-primary-color hover:text-primary-color/80 transition-colors duration-200 font-medium"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
                        </svg>
                        <span>View on GitHub</span>
                      </motion.a>
                    )}
                  </div>
                </>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects; 